import {
    type ViewProps,
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    AlertActionRadiusConstantType,
    AlertCloseRadiusConstantType,
    AlertLiveRegionConstantType,
    AlertColorsConstantType,
    AlertRadiusConstantType,
    AlertDynamicStyleType,
    AlertRoleConstantType,
    AlertColors,
    AlertType
} from "./type";
import {
    getTypeColors
} from "../../helpers/theme/typeColors";
import type {
    Mutable
} from "../../types";
import type {
    ButtonVariant
} from "../button/type";
import {
    getElevationStyle
} from "../../helpers/theme/elevation";
import {
    getStateColorKey
} from "../../helpers/theme/state";
import {
    concentric
} from "../../helpers/theme/concentric";
import {
    getCornerStyle
} from "../../helpers/theme/corner";
import {
    getHitArea
} from "../../helpers/theme/control";

export const ALERT_ICON_COLOR_KEYS: Record<AlertType, keyof NCoreUIKit.IconContentColors> = {
    primary: "emphasized",
    neutral: "default",
    success: "success",
    warning: "warning",
    danger: "danger",
    info: "info"
};

export const ALERT_ASSERTIVE_TYPES: Array<AlertType> = [
    "warning",
    "danger"
];

export const ALERT_ACTION_VARIANTS: Array<ButtonVariant> = [
    "outline",
    "ghost"
];

export const ALERT_ACTION_CONTAINER_COLORS: Array<string | undefined> = [
    "transparent",
    undefined
];

export const ALERT_MAX_ACTIONS = ALERT_ACTION_VARIANTS.length;

export const getAlertColors = ({
    variant,
    colors,
    type
}: AlertColorsConstantType): AlertColors => {
    const stateKey = getStateColorKey({
        type
    });

    const iconColorKey = ALERT_ICON_COLOR_KEYS[type];

    if(variant === "outline") {
        return {
            containerColor: colors.content.container.raised,
            titleColor: colors.system.onContainer[stateKey],
            iconColor: colors.content.icon[iconColorKey],
            descriptionColor: colors.content.text.mid,
            borderColor: getTypeColors({
                variant: "outline",
                colors,
                type
            }).borderColor,
            iconColorKey
        };
    }

    return {
        titleColor: colors.system.onContainer[stateKey],
        iconColor: colors.content.icon[iconColorKey],
        containerColor: colors.system.low[stateKey],
        descriptionColor: colors.content.text.high,
        borderColor: colors.system.high[stateKey],
        iconColorKey
    };
};

export const getAlertRole = ({
    announceBehaviour,
    role,
    type
}: AlertRoleConstantType): ViewProps["role"] => {
    if(role) {
        return role;
    }

    if(announceBehaviour === "none") {
        return undefined;
    }

    if(announceBehaviour === "assertive") {
        return "alert";
    }

    if(announceBehaviour === "polite") {
        return "status";
    }

    return ALERT_ASSERTIVE_TYPES.includes(type) ? "alert" : "status";
};

export const getAlertLiveRegion = ({
    ariaLive,
    role
}: AlertLiveRegionConstantType): ViewProps["aria-live"] => {
    if(ariaLive) {
        return ariaLive;
    }

    if(role === "alert") {
        return "assertive";
    }

    return role === "status" ? "polite" : undefined;
};

export const getAlertRadius = ({
    cardInset,
    isBanner,
    radiuses,
    borders
}: AlertRadiusConstantType): number => {
    if(isBanner) {
        return 0;
    }

    if(cardInset === undefined) {
        return radiuses.surface;
    }

    return concentric({
        inset: cardInset + borders.line,
        outer: radiuses.surface,
        role: radiuses.surface
    });
};

export const getAlertCloseRadius = ({
    controlHeights,
    typography,
    radiuses,
    borders,
    spaces
}: AlertCloseRadiusConstantType): number => {
    return concentric({
        inset: spaces.spacingMd + (typography.titleSmallSize.lineHeight - controlHeights.small) / 2 + borders.line,
        outer: radiuses.surface,
        role: radiuses.actions
    });
};

export const getAlertActionRadius = ({
    controlHeights,
    radiuses,
    borders
}: AlertActionRadiusConstantType): number => {
    return concentric({
        inset: (controlHeights.large - controlHeights.small) / 2 + borders.line,
        outer: radiuses.surface,
        role: radiuses.actions
    });
};

const stylesheet = StyleSheet.create({
    icon: {
        justifyContent: "center",
        alignItems: "center",
        flexShrink: 0
    },
    close: {
        alignSelf: "flex-start"
    },
    container: {
        flexDirection: "row",
        borderStyle: "solid"
    },
    endRow: {
        alignItems: "center",
        flexDirection: "row"
    },
    body: {
        flexShrink: 1,
        flexGrow: 1,
        minWidth: 0
    },
    endText: {
        flexShrink: 1,
        flexGrow: 1,
        minWidth: 0
    }
});

export const useStyles = ({
    controlPaddings,
    controlHeights,
    currentColors,
    shapeConfigs,
    touchTargets,
    pointerType,
    shadowAlpha,
    elevations,
    typography,
    cardInset,
    iconSizes,
    isCompact,
    isBanner,
    radiuses,
    borders,
    variant,
    colors,
    spaces
}: AlertDynamicStyleType) => {
    const lineHeight = typography.titleSmallSize.lineHeight;

    const hitArea = getHitArea({
        visualSize: controlHeights.small,
        touchTargets,
        pointerType
    });

    const styles = {
        body: {
            gap: isCompact ? controlPaddings.small : spaces.spacingXxs,
            flexDirection: isCompact ? "row" : "column",
            flexWrap: isCompact ? "wrap" : "nowrap"
        } as Mutable<ViewStyle>,
        close: {
            marginEnd: (iconSizes.small - hitArea.minWidth) / 2,
            marginVertical: (lineHeight - hitArea.minHeight) / 2
        } as Mutable<ViewStyle>,
        container: {
            backgroundColor: currentColors.containerColor,
            borderColor: currentColors.borderColor,
            paddingHorizontal: spaces.spacingMd,
            paddingVertical: spaces.spacingMd,
            ...getCornerStyle({
                radius: getAlertRadius({
                    cardInset,
                    isBanner,
                    radiuses,
                    borders
                }),
                shapeConfigs
            }),
            gap: controlPaddings.small,
            borderWidth: borders.line
        } as Mutable<ViewStyle>,
        icon: {
            width: iconSizes.small,
            height: lineHeight
        } as Mutable<ViewStyle>
    };

    if(variant === "outline" && !isBanner && elevations.level1.length) {
        Object.assign(styles.container, getElevationStyle({
            shadowColor: colors.system.shadow,
            level: "level1",
            shadowAlpha,
            elevations
        }));
    }

    if(isCompact) {
        styles.container.paddingVertical = (controlHeights.large - lineHeight) / 2 - borders.line;

        styles.container.minHeight = controlHeights.large;
    }

    if(isBanner) {
        styles.container.paddingHorizontal = spaces.spacingLg;

        delete styles.container.borderWidth;

        if(variant !== "banner") {
            styles.container.borderBottomWidth = borders.line;
        }
    }

    return styles;
};
export default stylesheet;
